/**
 * Bending Moment Diagram (BMD) Utilities
 * 
 * Modular design for calculating and generating BMD data
 * Currently uses hardcoded values but designed for future pipeline integration
 */

import * as THREE from 'three';

// Types for BMD calculation
export interface BeamProperties {
  id: string;
  length: number; // beam length in meters
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  supportType: 'simply-supported' | 'cantilever' | 'fixed' | 'continuous';
}

export interface BMDPoint {
  position: number; // position along beam (0 to 1)
  moment: number; // bending moment value in kN·m
}

export interface BMDData {
  beamId: string;
  points: BMDPoint[];
  maxMoment: number;
  maxMomentPosition: number;
  reactions: {
    start: number; // reaction at start (kN)
    end: number; // reaction at end (kN)
  };
  loadDescription: string;
}

/**
 * Generate hardcoded BMD data for demonstration
 * TODO: Replace with actual load pipeline integration
 */
export function generateHardcodedBMD(beam: BeamProperties): BMDData {
  // Hardcoded case: Single point load P = 24 kN at center
  // Reactions: RA = RB = 12 kN
  // Max bending moment: 36 kN·m at center
  
  const beamLength = beam.startPoint.distanceTo(beam.endPoint);
  const points: BMDPoint[] = [];
  
  // Generate BMD curve points for simply supported beam with center point load
  const numPoints = 50;
  for (let i = 0; i <= numPoints; i++) {
    const x = i / numPoints; // position ratio (0 to 1)
    const xActual = x * beamLength; // actual position in meters
    
    let moment = 0;
    
    if (x <= 0.5) {
      // Left half: M = RA * x = 12 * x
      moment = 12 * xActual;
    } else {
      // Right half: M = RB * (L - x) = 12 * (L - x)
      moment = 12 * (beamLength - xActual);
    }
    
    points.push({
      position: x,
      moment: moment
    });
  }
  
  return {
    beamId: beam.id,
    points,
    maxMoment: 36, // kN·m
    maxMomentPosition: 0.5, // at center
    reactions: {
      start: 12, // RA = 12 kN
      end: 12   // RB = 12 kN
    },
    loadDescription: "Single point load P = 24 kN at center"
  };
}

/**
 * Generate BMD data from custom input points
 */
export function generateBMDFromCustomData(
  beam: BeamProperties,
  customPoints: { position: number; moment: number }[]
): BMDData {
  if (!customPoints || customPoints.length === 0) {
    // Return empty BMD data
    return {
      beamId: beam.id,
      points: [
        { position: 0, moment: 0 },
        { position: 1, moment: 0 }
      ],
      maxMoment: 0,
      maxMomentPosition: 0.5,
      reactions: { start: 0, end: 0 },
      loadDescription: "No load applied"
    };
  }

  // Sort points by position
  const sortedPoints = [...customPoints].sort((a, b) => a.position - b.position);
  
  // Ensure we have points at the beam ends (position 0 and 1)
  const points: BMDPoint[] = [];
  
  // Add start point if not present
  if (sortedPoints[0].position > 0) {
    points.push({ position: 0, moment: 0 });
  }
  
  // Add all custom points
  points.push(...sortedPoints);
  
  // Add end point if not present
  if (sortedPoints[sortedPoints.length - 1].position < 1) {
    points.push({ position: 1, moment: 0 });
  }

  // Find maximum moment and its position
  let maxMoment = 0;
  let maxMomentPosition = 0.5;
  
  for (const point of points) {
    if (Math.abs(point.moment) > Math.abs(maxMoment)) {
      maxMoment = point.moment;
      maxMomentPosition = point.position;
    }
  }

  return {
    beamId: beam.id,
    points,
    maxMoment: Math.abs(maxMoment),
    maxMomentPosition,
    reactions: {
      start: 0, // TODO: Calculate from equilibrium if needed
      end: 0
    },
    loadDescription: `Custom BMD with ${customPoints.length} points`
  };
}

/**
 * Get BMD data for a beam - checks for custom data first, then falls back to hardcoded
 */
export function getBMDDataForBeam(
  beam: BeamProperties, 
  customBMDData?: { position: number; moment: number }[]
): BMDData {
  if (customBMDData && customBMDData.length > 0) {
    return generateBMDFromCustomData(beam, customBMDData);
  }
  
  // Fallback to hardcoded data for demonstration
  return generateHardcodedBMD(beam);
}

/**
 * Generate closed BMD shape geometry for proper fill rendering
 */
export function generateBMDClosedGeometry(
  bmdData: BMDData,
  beam: BeamProperties,
  scale: number = 0.01
): { vertices: Float32Array; indices: number[] } {
  const beamStart = beam.startPoint;
  const beamEnd = beam.endPoint;
  const beamDirection = new THREE.Vector3().subVectors(beamEnd, beamStart).normalize();
  
  // Calculate perpendicular direction for BMD offset
  let bmdDirection: THREE.Vector3;
  
  if (Math.abs(beamDirection.y) > 0.9) {
    bmdDirection = new THREE.Vector3(0, 0, 1);
  } else {
    const upDirection = new THREE.Vector3(0, 1, 0);
    const rightVector = new THREE.Vector3().crossVectors(beamDirection, upDirection).normalize();
    bmdDirection = new THREE.Vector3().crossVectors(rightVector, beamDirection).normalize();
    
    if (bmdDirection.y < 0) {
      bmdDirection.multiplyScalar(-1);
    }
  }
  
  // Generate vertices for closed BMD shape
  const vertices: number[] = [];
  const indices: number[] = [];
  
  // Create baseline points (beam line)
  const baselinePoints: THREE.Vector3[] = [];
  for (const point of bmdData.points) {
    const positionAlongBeam = beamStart.clone().lerp(beamEnd, point.position);
    baselinePoints.push(positionAlongBeam);
  }
  
  // Create BMD curve points
  const bmdPoints: THREE.Vector3[] = [];
  for (const point of bmdData.points) {
    const positionAlongBeam = beamStart.clone().lerp(beamEnd, point.position);
    const momentOffset = bmdDirection.clone().multiplyScalar(point.moment * scale);
    const bmdPoint = positionAlongBeam.clone().add(momentOffset);
    bmdPoints.push(bmdPoint);
  }
  
  // Add all vertices (baseline first, then BMD curve)
  baselinePoints.forEach(point => {
    vertices.push(point.x, point.y, point.z);
  });
  bmdPoints.forEach(point => {
    vertices.push(point.x, point.y, point.z);
  });
  
  const numPoints = bmdData.points.length;
  
  // Create triangulated face for the BMD area
  for (let i = 0; i < numPoints - 1; i++) {
    const baseIndex1 = i;
    const baseIndex2 = i + 1;
    const bmdIndex1 = numPoints + i;
    const bmdIndex2 = numPoints + i + 1;
    
    // Two triangles per quad segment
    indices.push(baseIndex1, baseIndex2, bmdIndex2);
    indices.push(baseIndex1, bmdIndex2, bmdIndex1);
  }
  
  return {
    vertices: new Float32Array(vertices),
    indices: indices
  };
}

/**
 * Generate cylindrical tube geometry for BMD outline
 */
export function generateBMDCylindricalOutline(
  bmdData: BMDData,
  beam: BeamProperties,
  scale: number = 0.01,
  tubeRadius: number = 0.002 // Small radius for pipe-like appearance
): { vertices: Float32Array; indices: number[] } {
  const beamStart = beam.startPoint;
  const beamEnd = beam.endPoint;
  const beamDirection = new THREE.Vector3().subVectors(beamEnd, beamStart).normalize();
  
  // Calculate perpendicular direction for BMD offset
  let bmdDirection: THREE.Vector3;
  
  if (Math.abs(beamDirection.y) > 0.9) {
    bmdDirection = new THREE.Vector3(0, 0, 1);
  } else {
    const upDirection = new THREE.Vector3(0, 1, 0);
    const rightVector = new THREE.Vector3().crossVectors(beamDirection, upDirection).normalize();
    bmdDirection = new THREE.Vector3().crossVectors(rightVector, beamDirection).normalize();
    
    if (bmdDirection.y < 0) {
      bmdDirection.multiplyScalar(-1);
    }
  }
  
  // Create BMD curve centerline points
  const centerlinePoints: THREE.Vector3[] = [];
  for (const point of bmdData.points) {
    const positionAlongBeam = beamStart.clone().lerp(beamEnd, point.position);
    const momentOffset = bmdDirection.clone().multiplyScalar(point.moment * scale);
    const bmdPoint = positionAlongBeam.clone().add(momentOffset);
    centerlinePoints.push(bmdPoint);
  }
  
  if (centerlinePoints.length < 2) {
    return { vertices: new Float32Array([]), indices: [] };
  }
  
  const vertices: number[] = [];
  const indices: number[] = [];
  const radialSegments = 8; // Number of sides for the cylindrical tube
  
  // Generate vertices for cylindrical tube
  for (let i = 0; i < centerlinePoints.length; i++) {
    const point = centerlinePoints[i];
    
    // Calculate local coordinate system for this point
    let tangent: THREE.Vector3;
    if (i === 0) {
      tangent = new THREE.Vector3().subVectors(centerlinePoints[i + 1], centerlinePoints[i]).normalize();
    } else if (i === centerlinePoints.length - 1) {
      tangent = new THREE.Vector3().subVectors(centerlinePoints[i], centerlinePoints[i - 1]).normalize();
    } else {
      tangent = new THREE.Vector3()
        .subVectors(centerlinePoints[i + 1], centerlinePoints[i - 1])
        .normalize();
    }
    
    // Create perpendicular vectors for the circular cross-section
    const up = new THREE.Vector3(0, 1, 0);
    let normal1 = new THREE.Vector3().crossVectors(tangent, up).normalize();
    if (normal1.length() < 0.1) {
      normal1 = new THREE.Vector3(1, 0, 0);
    }
    const normal2 = new THREE.Vector3().crossVectors(tangent, normal1).normalize();
    
    // Add vertices for circular cross-section at this point
    for (let j = 0; j < radialSegments; j++) {
      const angle = (j / radialSegments) * Math.PI * 2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      
      const offset = normal1.clone().multiplyScalar(cos * tubeRadius)
        .add(normal2.clone().multiplyScalar(sin * tubeRadius));
      
      const vertexPos = point.clone().add(offset);
      vertices.push(vertexPos.x, vertexPos.y, vertexPos.z);
    }
  }
  
  // Generate indices for cylindrical faces
  for (let i = 0; i < centerlinePoints.length - 1; i++) {
    for (let j = 0; j < radialSegments; j++) {
      const current = i * radialSegments + j;
      const next = i * radialSegments + ((j + 1) % radialSegments);
      const currentNext = (i + 1) * radialSegments + j;
      const nextNext = (i + 1) * radialSegments + ((j + 1) % radialSegments);
      
      // Two triangles per quad
      indices.push(current, next, nextNext);
      indices.push(current, nextNext, currentNext);
    }
  }
  
  return {
    vertices: new Float32Array(vertices),
    indices: indices
  };
}
